import React, { Fragment, useContext, useEffect } from 'react';

import { checkConditions } from './forms/_utils/conditional';
import { UserContext } from '../../context/UserContext';

import Html from './forms/Html'
import Hidden from './forms/Hidden'
import Input from './forms/Input'
import Checkbox from './forms/Checkbox'
import Checkboxes from './forms/Checkboxes'
import Select from './forms/Select'
import Textarea from './forms/Textarea'
import ButtonGroup from './forms/ButtonGroup'
import FloatingInput from './forms/FloatingInput'
import FloatingSelect from './forms/FloatingSelect'
import FloatingTextarea from './forms/FloatingTextarea'

import Autosuggest from './forms/Autosuggest'
import Attachments from './forms/Attachments'

import Access from './forms/Access'
import GroupSelection from './forms/GroupSelection'
import TypeSelection from './forms/TypeSelection'
import StatusBtnGroup from './forms/StatusBtnGroup'

import Icon from './forms/Icon'
import ColorPicker from './forms/ColorPicker'
import CheckboxSelect from './forms/CheckboxSelect'
import Redactor from './forms/Redactor'
import Article from './forms/Article'

import Repeat from './forms/Repeating'

import StylingMap from './forms/StylingMap'

// future: 
// range slider?
// time?
// url?

// product- specific
import WGSLocation from './forms/_products/sfo/Location';

// consider adding a Collapse thing to the column
// it could watch for a value and then would animate if the value exists

// if item has a schema, it is the appId of the options
// if the item TYPE is a schema, it means to nest/link the actual schema

// dynamic lists with Yes/No/N/A... 
// could you ... make it similar to the cols thing
// where you pass the options...?


// still to do:
// buttongroup
// checkbox
// checkboxes
// dropzone (?)
// html
// radio buttons

// how to use:
// <Generated id="yes-this-is-required" schema={schema} size="sm" pieces={{ register, control, setValue, watch, errors, toParent: fromChild }} />

const View = (props) => {
  const { disabled, schema, pieces } = props;
  const { schemas } = useContext(UserContext);
  const formId = props.id ? props.id : 'form';
  const layout = props.layout ? props.layout : 'vertical';
  const repeat = props.repeat ? props.repeat : null;
  pieces.schemas = schemas;
  pieces.size = props.size ? props.size : 'sm';
  pieces.formId = formId;

  useEffect(() => {
    // set any manual form values (i.e. values not sent in the active object/record)
    // mainly using this in "new" forms where we want to pass default values
    if(!pieces.vals) return;
    for (const [key, value] of Object.entries(pieces.vals)) {
      pieces.setValue(key, value);
    }
  }, [pieces.vals, pieces.setValue])

  return (
    <Fragment>
      { schema?.map((item) => findType({ formId, layout, repeat, item, pieces, disabled }) )}
    </Fragment>
  )
}

const findType = (props) => {
  const { formId, layout, repeat, item: ogItem, pieces, disabled } = props;
  if(!ogItem) return;
  let item = {...ogItem};
  if(disabled) item.disabled = true;

  if(repeat) {
    if(item.field) {
      // override repeated rows with their parent + index values
      item.field = `${repeat.parent}.${repeat.idx}.${item.field}`;
    }
  }

  item.errorMsg = (pieces.errors[item.field] && pieces.errors[item.field].message) ? pieces.errors[item.field].message : 'This field is required';

  let display = item.conditional ? checkConditions(item, pieces.watch) : true;
  if(display === 'error') return <div key={formId+item.id} className="bg-danger text-white p-3">Conditional logic error.</div>
  if(!display) return;

  if(item.type.includes('col-'))
    return <Col key={formId+item.id} formId={formId} layout={layout} repeat={repeat} obj={item} pieces={pieces} disabled={disabled} />;

  if(item.type === 'repeating')
    return <Repeating key={formId+item.id} formId={formId} layout={layout} obj={item} pieces={pieces} disabled={disabled} />;
  
  if(item.schema) {
    let found = pieces.schemas.find(x => x.appId === item.schema.appId);
    if(found) {
      let opts = found.schema;
      if(item.schema.label && item.schema.label !== 'label') {
        // override/select label if needed
        opts = found.schema.map(x => ({ value: x.value, label: x[item.schema.label] }));
      }
      item.options = opts;
    }
  }

  if(item.dynamic && item.options?.length>0) {
    // if item is dynamic, we are filtering the options based on another field
    // this assumes that any dynamic ones will include options
    console.clear();
    let field = pieces.watch(item.dynamic);
    item.options = item.options.filter(x => field?.includes(x.value));
    console.log(item);
    console.log(field);
  }

  switch (item.type) {
    case 'repeat':
      return <Repeat key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />
    case 'input':
      return <Input key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'floating-input':
      return <FloatingInput key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'button-group':
      return <ButtonGroup key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'status-button-group':
      return <StatusBtnGroup key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'checkbox':
      return <Checkbox key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'checkboxes':
      return <Checkboxes key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'select':
      return <Select key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'floating-select':
      return <FloatingSelect key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'autosuggest':
      return <Autosuggest key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'textarea':
      return <Textarea key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'floating-textarea':
      return <FloatingTextarea key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'hidden':
      return <Hidden key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'group-selection':
      return <GroupSelection key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'type-selection':
      return <TypeSelection key={formId+item.id} item={item} pieces={pieces} />;
    case 'access':
      return <Access key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'attachments':
      return <Attachments key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'checkbox-select':
      return <CheckboxSelect key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'color-picker':
      return <ColorPicker key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'redactor':
      return <Redactor key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'article':
      return <Article key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'wgs-location':
      return <WGSLocation key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'styling-map':
      return <StylingMap key={formId+item.id} {...pieces.register(item.field)} item={item} pieces={pieces} />;
    case 'icon':
      return <Icon key={formId+item.id} item={item} pieces={pieces} />;
    case 'html':
      return <Html key={formId+item.id} item={item} />;
    default:
      return <div key={formId+item.id} className="bg-danger text-white p-3">type not found: {item.type}</div>;
  }
}

const Col = (props) => {
  const { formId, obj, layout, repeat, pieces, disabled } = props;

  if(layout==='horizontal') {
    // need to filter out hidden columns and append below table
    let hidden = obj.col.filter(x => x.type === 'hidden');
    let visible = obj.col.filter(x => x.type !== 'hidden');

    return (
      <Fragment>
        <table className="table table-borderless table-sm my-0">
          <tbody>
            <tr>
            { visible.map((item) => (
              <td key={formId+item.id} style={{ width: item.width ? `${item.width}px` : 'auto'}}>
                { findType({ formId, item, layout, repeat, pieces, disabled }) }
              </td>
            ))}
            </tr>
          </tbody>
        </table>
        { hidden.map(item => findType({ formId, item, layout, repeat, pieces }))}
      </Fragment>
    )
  } else {
    return (
      <div className="row g-1">
        { obj.type === 'col-336' && (
          <Fragment>
            <div className="col-3">
              { obj.col1?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
            <div className="col-3">
              { obj.col2?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
            <div className="col-6">
              { obj.col3?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
          </Fragment>
        )}

        { obj.type === 'col-363' && (
          <Fragment>
            <div className="col-3">
              { obj.col1?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
            <div className="col-6">
              { obj.col2?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
            <div className="col-3">
              { obj.col3?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
          </Fragment>
        )}

        { obj.type === 'col-4' && (
          <Fragment>
            <div className="col-4">
              { obj.col1?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
            <div className="col-4">
              { obj.col2?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
            <div className="col-4">
              { obj.col3?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
          </Fragment>
        )}

        { obj.type === 'col-48' && (
          <Fragment>
            <div className="col-4">
              { obj.col1?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
            <div className="col-8">
              { obj.col2?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
          </Fragment>
        )}

        { obj.type === 'col-84' && (
          <Fragment>
            <div className="col-8">
              { obj.col1?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
            <div className="col-4">
              { obj.col2?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
          </Fragment>
        )}

        { obj.type === 'col-6' && (
          <Fragment>
            <div className="col-6">
              { obj.col1?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
            <div className="col-6">
              { obj.col2?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
          </Fragment>
        )}

        { obj.type === 'col-633' && (
          <Fragment>
            <div className="col-6">
              { obj.col1?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
            <div className="col-3">
              { obj.col2?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
            <div className="col-3">
              { obj.col3?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
            </div>
          </Fragment>
        )}
  
        { obj.type === 'col-12' && (
          <div className="col">
            { obj.col?.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
          </div>
        )}
      </div>
    )
  }
}

const Repeating = (props) => {
  const { formId, obj, layout, repeat,  pieces, disabled } = props;
  let id = Number(obj.id);
  let idx = Number(obj.startIdx);

  const res = [...Array(obj.qty)].map((_, loopIdx) => {
    let repeated = {};
    for(const schema of obj.schema) {
      id++;
      repeated = {...schema, id: String(id)};

      if(schema.col) {
        let col = [];
        for(const item of repeated.col) {
          id++;
          col.push({
            ...item, 
            id: String(id), 
            label: item.label.replace('[X]', loopIdx+1),
            array: obj.field, 
            field: `${obj.field}[${idx}]`,
            index: idx,
            disabled
          });
        }
        repeated.col = col;
      }

      if(schema.col1) {
        let col1 = [];
        for(const item of repeated.col1) {
          id++;
          col1.push({
            ...item, 
            id: String(id), 
            label: item.label.replace('[X]', loopIdx+1),
            array: obj.field, 
            field: `${obj.field}[${idx}]`,
            index: idx,
            disabled
          });
          idx++;
        }
        repeated.col1 = col1;
      }

      if(schema.col2) {
        let col2 = [];
        for(const item of repeated.col2) {
          id++;
          col2.push({
            ...item, 
            id: String(id), 
            label: item.label.replace('[X]', loopIdx+1),
            array: obj.field, 
            field: `${obj.field}[${idx}]`,
            index: idx,
            disabled
          });
          idx++;
        }
        repeated.col2 = col2;

        if(schema.col3) {
          let col3 = [];
          for(const item of repeated.col3) {
            id++;
            col3.push({
              ...item, 
              id: String(id), 
              label: item.label.replace('[X]', loopIdx+1),
              array: obj.field, 
              field: `${obj.field}[${idx}]`,
              index: idx,
              disabled
            });
            idx++;
          }
          repeated.col3 = col3;
        }
      }
    }

    // console.log('next available idx:', idx);
    return repeated;
  });

  return (
    <div>
      { res.map((item) => findType({ formId, item, layout, repeat, pieces, disabled }) )}
    </div>
  )
;}

export default View;
